/* eslint-disable max-lines */
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { useState, useMemo, type ReactNode } from 'react';
import PageHeader from '@components/layout/page-header';
import Button from '@components/atoms/ui-kit/button.tsx';
import { Breadcrumbs } from '@components/atoms/ui-kit/breadcrumbs.tsx';
import { PATHS } from '../../router/paths';
import { useGetGlossary } from '@utils/api/glossaries/use-get-glossary';
import { useGetGlossaryEntries } from '@utils/api/glossaries/use-get-glossary-entries';
import EditGlossaryModal from '@components/glossaries/editGlossaryModal';
import DeleteGlossaryModal from '@components/glossaries/deleteGlossaryModal';
import {
  Edit,
  Download,
  Trash2,
  ArrowLeft,
  Languages,
  Hash,
  Activity,
  Calendar
} from 'lucide-react';
import DataGridV2, {
  type DataGridColumn
} from '@components/atoms/datatable/data-grid-v2';
import { Skeleton } from '@components/atoms/ui-kit/skeleton';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { UpdateGlossaryRequest, GlossaryEntry } from '@utils/types/glossary';
import { GET_URL } from '@utils/api/endpoints';
import { fmtDate } from '@utils/format';
import { useThemeContext } from '@utils/hooks/use-theme-context';
import { Badge } from '@components/atoms/ui-kit/badge.tsx';
import { Card } from '@components/atoms/ui-kit/card.tsx';
import { Heading, Text } from '@components/atoms/ui-kit/text.tsx';

type Params = { glossaryId: string };

type Row = {
  id: number;
  source_term: string;
  target_term: string;
};

type MetricCardProps = {
  icon: ReactNode;
  label: string;
  children: ReactNode;
  iconBg: string;
  iconBorder: string;
  iconColor: string;
};

function MetricCard({
  icon,
  label,
  children,
  iconBg,
  iconBorder,
  iconColor
}: MetricCardProps) {
  return (
    <Card padding="sm" className="flex items-start gap-3">
      <div
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl"
        style={{
          backgroundColor: iconBg,
          color: iconColor,
          border: `1px solid ${iconBorder}`
        }}
        aria-hidden="true"
      >
        {icon}
      </div>
      <div className="min-w-0 flex flex-col gap-1">
        <Text
          as="div"
          size="xs"
          weight="medium"
          tone="muted"
          className="uppercase tracking-wide"
        >
          {label}
        </Text>
        <Text as="div" size="base" weight="semibold">
          {children}
        </Text>
      </div>
    </Card>
  );
}

export default function GlossaryDetails() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { glossaryId: glossaryIdParam } = useParams<Params>();
  const glossaryId = Number(glossaryIdParam || 0);
  const { getColour, getBrandTint } = useThemeContext();

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const { data: glossary, isLoading: isLoadingGlossary } =
    useGetGlossary(glossaryId);
  const { data: entries, isLoading: isLoadingEntries } =
    useGetGlossaryEntries(glossaryId);
  const isLoading = isLoadingGlossary || isLoadingEntries;

  const updateSchema = Yup.object().shape({
    name: Yup.string().max(100, t('glossary-name-max-length')).optional(),
    description: Yup.string().optional(),
    is_active: Yup.boolean().optional()
  });

  const {
    control: editControl,
    handleSubmit: handleEditSubmit,
    reset: resetEdit,
    register: registerEdit,
    formState: { errors: editErrors }
  } = useForm<UpdateGlossaryRequest>({
    resolver: yupResolver(updateSchema)
  });

  const rows = useMemo<Row[]>(() => {
    if (!entries) return [];
    return entries.map((entry: GlossaryEntry) => ({
      id: entry.id,
      source_term: entry.source_term,
      target_term: entry.target_term
    }));
  }, [entries]);

  const handleEdit = () => {
    if (!glossary) return;
    resetEdit({
      name: glossary.name,
      description: glossary.description || '',
      is_active: glossary.is_active
    });
    setIsEditModalOpen(true);
  };

  const handleDownloadEntries = async () => {
    if (!glossary) return;
    try {
      const url = GET_URL(glossaryId.toString()).glossaryEntries;
      if (!url) return;

      const response = await fetch(url, { credentials: 'include' });
      const csvText = await response.text();

      const blob = new Blob([csvText], { type: 'text/csv' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `${glossary.name.replace(/\s+/g, '_')}_glossary.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch {
      // Failed to download glossary entries
    }
  };

  const columns = useMemo<DataGridColumn<Row>[]>(() => {
    return [
      {
        id: 'source_term',
        header: t('source-term'),
        field: 'source_term',
        minWidth: 250,
        enableSorting: true,
        enableFiltering: true,
        filterVariant: 'text',
        cell: ({ getValue }) => (
          <Text as="span" size="sm">
            {String(getValue() ?? '')}
          </Text>
        )
      },
      {
        id: 'target_term',
        header: t('target-term'),
        field: 'target_term',
        minWidth: 250,
        enableSorting: true,
        enableFiltering: true,
        filterVariant: 'text',
        cell: ({ getValue }) => (
          <Text as="span" size="sm">
            {String(getValue() ?? '')}
          </Text>
        )
      }
    ];
  }, [t]);

  if (isLoading) {
    return (
      <div className="bg-app min-h-screen">
        <div className="space-y-4 px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 pt-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} variant="card" />
            ))}
          </div>
          <Skeleton variant="table" rows={8} />
        </div>
      </div>
    );
  }

  if (!glossary) {
    return (
      <div className="bg-app min-h-screen flex items-center justify-center">
        <Text as="div" size="lg">
          {t('glossary-not-found')}
        </Text>
      </div>
    );
  }

  const statusActive = glossary.is_active;
  const showEmptyEntries = rows.length === 0 && !isLoading;

  const iconColors = {
    iconBg: getBrandTint(0.12),
    iconBorder: getBrandTint(0.25),
    iconColor: getColour('activeText')
  };

  return (
    <div className="bg-app min-h-screen">
      <div className="space-y-4 px-4">
        <EditGlossaryModal
          glossaryId={glossary.id}
          isModalOpen={isEditModalOpen}
          setIsModalOpen={setIsEditModalOpen}
          handleSubmit={handleEditSubmit}
          control={editControl}
          errors={editErrors}
          register={registerEdit}
        />

        <DeleteGlossaryModal
          glossary={glossary}
          isModalOpen={isDeleteModalOpen}
          setIsModalOpen={setIsDeleteModalOpen}
          onDeleteSuccess={() => navigate(PATHS().GLOSSARIES)}
        />

        <PageHeader
          title={
            <Breadcrumbs
              items={[]}
              currentFolderName={glossary.name}
              rootLabel={t('glossaries')}
              rootPath={PATHS().GLOSSARIES}
            />
          }
          caption={glossary.description || t('glossary-details')}
        >
          <div className="flex gap-2">
            <Button
              onClick={() => navigate(PATHS().GLOSSARIES)}
              icon={<ArrowLeft width={14} />}
              label={t('back')}
              size="small"
              variant="smoke"
            />
            <Button
              onClick={handleEdit}
              icon={<Edit width={14} />}
              label={t('edit')}
              size="small"
            />
            <Button
              onClick={handleDownloadEntries}
              icon={<Download width={14} />}
              label={t('download-csv')}
              size="small"
            />
            <Button
              onClick={() => setIsDeleteModalOpen(true)}
              icon={<Trash2 width={14} />}
              label={t('delete')}
              size="small"
              variant="danger"
            />
          </div>
        </PageHeader>

        {/* Metric cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          <MetricCard
            icon={<Languages className="h-5 w-5" />}
            label={t('language-pair')}
            {...iconColors}
          >
            <span className="font-mono">
              {glossary.source_language} → {glossary.target_language}
            </span>
          </MetricCard>

          <MetricCard
            icon={<Hash className="h-5 w-5" />}
            label={t('entry-count')}
            {...iconColors}
          >
            {glossary.entry_count}
          </MetricCard>

          <MetricCard
            icon={<Activity className="h-5 w-5" />}
            label={t('status')}
            {...iconColors}
          >
            <Badge color={statusActive ? 'green' : 'zinc'}>
              {statusActive ? t('active') : t('inactive')}
            </Badge>
          </MetricCard>

          <MetricCard
            icon={<Calendar className="h-5 w-5" />}
            label={t('created')}
            {...iconColors}
          >
            {fmtDate(glossary.created_at)}
          </MetricCard>
        </div>

        {/* Entries card */}
        <Card padding="none">
          <div className="p-4 border-b border-hairline">
            <Heading level={3} size="lg">
              {t('glossary-entries')}
            </Heading>
            <Text as="p" size="sm" tone="muted">
              {t('glossary-entries-description')}
            </Text>
          </div>

          <div className="p-4 relative">
            <DataGridV2<Row>
              data={rows}
              columns={columns}
              enableRowSelection={false}
              enablePagination
              enableSorting
              enableFiltering
              showColumnFilters={false}
              filterMode="popover"
              pageSize={50}
              pageSizeOptions={[25, 50, 75, 100]}
              height="60vh"
              dense
              grid={false}
              striped
              loading={isLoading}
              getRowId={row => String(row.id)}
            />

            {showEmptyEntries && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 pointer-events-none">
                <div className="pointer-events-auto flex flex-col items-center gap-3 mt-8">
                  <Text as="p" size="sm" tone="muted">
                    {t('no-glossary-entries-yet')}
                  </Text>
                </div>
              </div>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
